import { Component, input, inject, signal, computed } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { Auth } from '@angular/fire/auth';
import { Order } from '../order.model';
import { PhotoPreview } from '../photo-preview/photo-preview';
import { DownloadService } from '../download.service';
import { OrdersService } from '../orders.service';
import { OrderLocationComponent } from '../order-location/order-location.component';
import { OrderDeletedComponent } from '../order-deleted/order-deleted.component';
import { OrderPropertiesComponent } from '../order-properties/order-properties.component';
import { OrderCommentsComponent } from '../order-comments/order-comments.component';
import { getOrderStatusOption } from '../order-status';
import { IconComponent } from '../../../shared/icon/icon.component';

@Component({
  selector: 'app-order-item',
  standalone: true,
  imports: [CommonModule, PhotoPreview, OrderLocationComponent, OrderDeletedComponent, OrderPropertiesComponent, OrderCommentsComponent, IconComponent],
  templateUrl: './order-item.html',
  styleUrl: './order-item.css'
})
export class OrderItem {
  order = input.required<Order>();
  
  private readonly router = inject(Router);
  private readonly downloadService = inject(DownloadService);
  private readonly auth = inject(Auth);
  public readonly ordersService = inject(OrdersService);

  downloading = signal(false);
  showComments = signal(false);

  unseenCommentsCount = computed(() => {
    const userId = this.auth.currentUser?.uid;
    if (!userId) return 0;
    return this.ordersService.getUnseenCommentsCount(this.order(), userId);
  });

  statusOption = computed(() => getOrderStatusOption(this.order().status));

  navigateToDetails(): void {
    console.log("Navigating to order details for:", this.order().id);
    console.log("Current URL:", window.location.href);
    
    this.router.navigate(['/main/orders', this.order().id]).then(
      (success) => console.log('Navigation success:', success),
      (error) => console.error('Navigation failed:', error)
    );
  }

  navigateToUser(event: Event): void {
    const userId = this.order().userId;
    console.log("Navigating to user details for:", userId);
    
    this.router.navigate(['/main/users', userId]).then(
      (success) => console.log('User navigation success:', success),
      (error) => console.error('User navigation failed:', error)
    );
  }

  photos = computed(() => this.order().photos ? Object.values(this.order().photos) : []);

  async downloadPhotos(): Promise<void> {
    if (this.downloading()) return;

    this.downloading.set(true);
    const order = this.order();
    
    try {
      await this.downloadService.downloadOrderPhotos(order);
    } finally {
      this.downloading.set(false);
    }
  }

  onStatusChange(event: Event): void {
    const target = event.target as HTMLSelectElement;
    this.updateOrderStatus(target.value);
  }

  async updateOrderStatus(newStatus: string): Promise<void> {
    try {
      await this.ordersService.updateOrderStatus(this.order().id, newStatus);
      console.log('Order status updated to:', newStatus);
    } catch (error) {
      console.error('Failed to update order status:', error);
      alert('Failed to update order status.');
    }
  }

  async openComments(): Promise<void> {
    this.showComments.set(true);
    await this.ordersService.markCommentsAsSeen(this.order().id);
  }

  closeComments(): void {
    this.showComments.set(false);
  }
}
